/**
 * ARK secondary pages: small, immediate behaviours. No animation waits on these.
 *
 * <ark-support>: type to filter topics and questions (all words must match), topic chips,
 *   a live result count, a way out when nothing matches. ?q= in the URL pre-fills it.
 * <ark-size-guide>: tabs (arrow keys, #hash selects a chart), cm / in switch, and a size
 *   finder (type a chest or head measurement, the matching size column is marked).
 * <ark-care>: "What are you caring for?" narrows the manual to one kind of garment; the
 *   chapter index marks the chapter being read.
 * <ark-environments>: the word at the middle of the screen (or under the pointer) brings
 *   up its photo.
 * <ark-contact>: topic choice shows its hint and the order field; validation in place,
 *   on submit, with messages tied to the fields. ?topic=return preselects a topic.
 */

class ArkSupport extends HTMLElement {
  connectedCallback() {
    // The search field lives in the section, or in the page hero above it.
    /** @param {string} name */
    const find = (name) => this.querySelector(`[ref="${name}"]`) || document.querySelector(`[data-ark-support="${name}"]`);
    this.input = /** @type {HTMLInputElement | null} */ (find('input'));
    this.status = find('status');
    this.clear = find('clear');
    this.empty = this.querySelector('[ref="empty"]');
    this.items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-item]')]);
    this.groups = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-group]')]);
    this.extras = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-hide-on-search]')]);
    this.topic = '';

    this.querySelectorAll('[data-topic][aria-pressed]').forEach((chip) =>
      chip.addEventListener('click', () => {
        this.topic = /** @type {HTMLElement} */ (chip).dataset.topic || '';
        this.querySelectorAll('[data-topic][aria-pressed]').forEach((other) =>
          other.setAttribute('aria-pressed', String(other === chip))
        );
        this.#filter();
      })
    );

    // A link to one question (#q-...) opens it.
    this.#openFromHash();
    window.addEventListener('hashchange', () => this.#openFromHash());

    if (!this.input) return;
    const query = new URLSearchParams(location.search).get('q');
    if (query) this.input.value = query;
    this.input.addEventListener('input', () => this.#filter());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.input?.value) {
        this.input.value = '';
        this.#filter();
      }
    });
    this.clear?.addEventListener('click', () => {
      if (!this.input) return;
      this.input.value = '';
      this.#filter();
      this.input.focus();
    });
    if (query) this.#filter();
  }

  #openFromHash() {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id.startsWith('q-')) return;
    const target = document.getElementById(id);
    if (target instanceof HTMLDetailsElement && this.contains(target)) {
      target.open = true;
      target.querySelector('summary')?.focus({ preventScroll: true });
    }
  }

  #filter() {
    const words = (this.input?.value || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
    let shown = 0;
    let questions = 0;
    for (const item of this.items) {
      const text = item.dataset.text || '';
      const inTopic = !this.topic || !item.dataset.topic || item.dataset.topic === this.topic;
      const isTopicCard = !item.dataset.topic;
      const matches = words.every((word) => text.includes(word));
      const visible = isTopicCard ? matches && !this.topic : matches && inTopic;
      item.hidden = !visible;
      if (visible) {
        shown += 1;
        if (!isTopicCard) questions += 1;
      }
      // Open the answers that match a search, close them again when it is cleared.
      if (item instanceof HTMLDetailsElement) item.open = words.length > 0 && visible && questions <= 3;
    }
    for (const group of this.groups) {
      group.hidden = !group.querySelector('[data-item]:not([hidden])');
    }
    const active = words.length > 0 || this.topic;
    for (const extra of this.extras) extra.hidden = Boolean(active);
    if (this.clear) /** @type {HTMLElement} */ (this.clear).hidden = words.length === 0;
    if (this.empty) /** @type {HTMLElement} */ (this.empty).hidden = !active || shown > 0;
    if (this.status) {
      const template = shown === 1 ? this.status.getAttribute('data-template-one') : this.status.getAttribute('data-template-other');
      this.status.textContent = active ? (template || '').replace('[count]', String(shown)) : '';
    }
  }
}

class ArkSizeGuide extends HTMLElement {
  connectedCallback() {
    this.currentUnit = 'cm';
    this.tabs = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (event.key === 'Home') this.select(0, true);
        else if (event.key === 'End') this.select(this.tabs.length - 1, true);
        else if (step) this.select((index + step + this.tabs.length) % this.tabs.length, true);
      });
    });
    this.querySelectorAll('[data-unit]').forEach((button) =>
      button.addEventListener('click', () => this.unit(/** @type {HTMLElement} */ (button).dataset.unit || 'cm'))
    );
    this.finderInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="finderInput"]'));
    this.finderInput?.addEventListener('input', () => this.#find());
    const hash = location.hash.slice(1);
    const fromHash = this.tabs.findIndex((tab) => tab.dataset.hash === hash);
    this.select(fromHash > 0 ? fromHash : 0);
  }

  /**
   * @param {number} index
   * @param {boolean} [focus]
   */
  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !on;
    });
    if (focus) this.tabs[index]?.focus();
    this.#find();
  }

  /** @param {string} unit */
  unit(unit) {
    this.currentUnit = unit;
    this.querySelectorAll('[data-unit]').forEach((button) =>
      button.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (button).dataset.unit === unit))
    );
    this.querySelectorAll('td[data-cm]').forEach((cell) => {
      const cm = /** @type {HTMLElement} */ (cell).dataset.cm || '';
      cell.textContent = unit === 'in' ? toInches(cm) : cm;
    });
    const unitLabel = this.querySelector('[ref="finderUnit"]');
    if (unitLabel) unitLabel.textContent = unit;
    if (this.finderInput && this.finderInput.value) {
      const value = parseFloat(this.finderInput.value);
      const converted = unit === 'in' ? value / 2.54 : value * 2.54;
      this.finderInput.value = String(Math.round(converted * 2) / 2);
    }
    this.#find();
  }

  /**
   * Size finder: the first row of the open chart (chest, or head for toques) against the
   * number typed. Marks the matching column and says the size in words.
   */
  #find() {
    const finder = /** @type {HTMLElement | null} */ (this.querySelector('[ref="finder"]'));
    const result = this.querySelector('[ref="finderResult"]');
    const panel = this.querySelector('[role="tabpanel"]:not([hidden])');
    if (!finder || !result || !panel) return;
    this.querySelectorAll('.is-match').forEach((cell) => cell.classList.remove('is-match'));

    const row = panel.querySelector('tbody tr');
    const headers = [...panel.querySelectorAll('thead th[data-col]')];
    const cells = /** @type {HTMLElement[]} */ ([...(row?.querySelectorAll('td[data-cm]') || [])]);
    const measure = row?.querySelector('th')?.textContent?.trim() || '';
    const measureLabel = finder.querySelector('[ref="finderMeasure"]');
    if (measureLabel) measureLabel.textContent = measure ? ` (${measure.toLowerCase()})` : '';
    if (row instanceof HTMLElement) finder.dataset.line = row.dataset.line || '';

    if (headers.length < 2) {
      // One size: nothing to find.
      result.textContent = (result.getAttribute('data-template-one') || '').replace('[size]', headers[0]?.textContent?.trim() || '');
      if (this.finderInput) this.finderInput.disabled = true;
      return;
    }
    if (this.finderInput) this.finderInput.disabled = false;

    const typed = parseFloat(this.finderInput?.value || '');
    if (!typed || typed <= 0) {
      result.innerHTML = result.getAttribute('data-hint-html') || result.innerHTML;
      if (!result.getAttribute('data-hint-html')) result.setAttribute('data-hint-html', result.innerHTML);
      return;
    }
    const cm = this.currentUnit === 'in' ? typed * 2.54 : typed;
    const ranges = cells.map((cell) => {
      const numbers = (cell.dataset.cm || '').match(/\d+(?:[.,]\d+)?/g)?.map((n) => parseFloat(n.replace(',', '.'))) || [];
      return { low: Math.min(...numbers), high: Math.max(...numbers) };
    });
    const name = (/** @type {number} */ index) => headers[index]?.textContent?.trim() || '';
    let index = ranges.findIndex((range) => cm >= range.low && cm <= range.high);
    let template = 'data-template-size';
    if (index === -1) {
      if (cm > ranges[ranges.length - 1].high) {
        template = 'data-template-over';
        index = ranges.length - 1;
      } else if (cm < ranges[0].low) {
        template = 'data-template-under';
        index = 0;
      } else {
        // Between two ranges: suggest the larger, which leaves room for layers.
        index = ranges.findIndex((range) => cm < range.low);
        template = 'data-template-between';
      }
    }
    const text = (result.getAttribute(template) || '').replace('[size]', name(index)).replace('[smaller]', name(index - 1));
    result.innerHTML = text.replace(name(index), `<strong>${name(index)}</strong>`);
    const column = String(index + 1);
    panel.querySelectorAll(`[data-col="${column}"]`).forEach((cell) => cell.classList.add('is-match'));
  }
}

/**
 * "86–91" (cm) → "34–36" (in), to the nearest half inch; text without numbers stays.
 * @param {string} value
 */
export function toInches(value) {
  return value.replace(/\d+(?:[.,]\d+)?/g, (number) => {
    const inches = Math.round((parseFloat(number.replace(',', '.')) / 2.54) * 2) / 2;
    return String(inches);
  });
}

class ArkContact extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    const radios = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[name="contact[Topic]"]')]);
    const preset = new URLSearchParams(location.search).get('topic');
    const match = radios.find((radio) => radio.dataset.key === preset);
    if (match) match.checked = true;
    radios.forEach((radio) => radio.addEventListener('change', () => this.#topic()));
    this.#topic();

    this.form.addEventListener('submit', (event) => {
      if (!this.#validate()) event.preventDefault();
    });
    this.form.querySelectorAll('input, textarea').forEach((field) =>
      field.addEventListener('blur', () => {
        if (field.getAttribute('aria-invalid') === 'true') this.#validate(false);
      })
    );
  }

  #topic() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="contact[Topic]"]:checked'));
    const key = checked?.dataset.key;
    this.querySelectorAll('[data-hint]').forEach((hint) => {
      /** @type {HTMLElement} */ (hint).hidden = /** @type {HTMLElement} */ (hint).dataset.hint !== key;
    });
    const order = /** @type {HTMLElement | null} */ (this.querySelector('[data-order-field]'));
    if (order) order.hidden = checked?.dataset.order !== 'true';
    if (checked) this.#error('topic', false);
  }

  /** @param {boolean} [focus] */
  #validate(focus = true) {
    const email = /** @type {HTMLInputElement} */ (this.querySelector('#ArkContactEmail'));
    const body = /** @type {HTMLTextAreaElement} */ (this.querySelector('#ArkContactBody'));
    const topic = this.querySelector('input[name="contact[Topic]"]:checked');
    const problems = [];
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
    this.#field(email, 'ArkContactEmailError', !emailOk) && problems.push(email);
    this.#field(body, 'ArkContactBodyError', body.value.trim().length < 2) && problems.push(body);
    if (!topic) {
      this.#error('topic', true);
      problems.unshift(/** @type {HTMLElement} */ (this.querySelector('input[name="contact[Topic]"]')));
    }
    if (focus && problems[0]) problems[0].focus();
    return problems.length === 0;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {string} errorId
   * @param {boolean} invalid
   */
  #field(field, errorId, invalid) {
    field.setAttribute('aria-invalid', String(invalid));
    const error = document.getElementById(errorId);
    if (error) error.hidden = !invalid;
    return invalid;
  }

  /**
   * @param {string} name
   * @param {boolean} show
   */
  #error(name, show) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector(`[data-error-for="${name}"]`));
    if (error) error.hidden = !show;
  }
}

class ArkCare extends HTMLElement {
  connectedCallback() {
    const chips = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('button[data-fabric]')]);
    const parts = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.ark-care__card[data-fabric], .ark-care__applies[data-fabric]')]);
    chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        const fabric = chip.dataset.fabric || '';
        chips.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
        parts.forEach((part) => (part.hidden = Boolean(fabric) && part.dataset.fabric !== fabric));
        if (fabric) this.dataset.filter = fabric;
        else delete this.dataset.filter;
      })
    );

    // The index marks the chapter being read.
    const links = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('.ark-care__index a')]);
    if (!links.length || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          links.forEach((link) => link.setAttribute('aria-current', String(link.hash === `#${entry.target.id}`)));
        }
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    this.querySelectorAll('.ark-care__chapter[id]').forEach((chapter) => this.observer?.observe(chapter));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class ArkEnvironments extends HTMLElement {
  connectedCallback() {
    const items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-env]')]);
    const photos = [...this.querySelectorAll('.ark-env__photo')];
    /** @param {number} index */
    const show = (index) => {
      items.forEach((item, i) => item.toggleAttribute('data-active', i === index));
      photos.forEach((photo, i) => photo.classList.toggle('is-active', i === index));
    };
    show(0);
    items.forEach((item, i) => {
      item.addEventListener('pointerenter', () => show(i));
      item.addEventListener('focusin', () => show(i));
    });
    if (!('IntersectionObserver' in window)) return;
    // The word crossing the middle of the screen brings its photo up.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) show(items.indexOf(/** @type {HTMLElement} */ (entry.target)));
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    items.forEach((item) => this.observer?.observe(item));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('ark-environments')) customElements.define('ark-environments', ArkEnvironments);
if (!customElements.get('ark-care')) customElements.define('ark-care', ArkCare);
if (!customElements.get('ark-support')) customElements.define('ark-support', ArkSupport);
if (!customElements.get('ark-size-guide')) customElements.define('ark-size-guide', ArkSizeGuide);
if (!customElements.get('ark-contact')) customElements.define('ark-contact', ArkContact);
